import { FSCollections } from '@src/constants/database';
import type {
  EventElasticSearchSummarySource,
  EventSearchIdsParams,
  EventSearchIdsResult,
  EventSearchSummariesParams,
  EventSearchSummariesResult,
} from '@src/models/interfaces/event.interface';
import type { ElasticQueryParams } from '@src/models/interfaces/general/ElasticQueryParams.interface';
import { EventMapper } from '@src/models/mappers/event.mapper';
import EventRepository from '@src/repositories/EventRepository';
import { buildPartialMatch } from '@src/util/elastic/buildPartialMatch';
import safeExecute from '@src/util/safeExecute';

import { buildTextSearch } from './utils';

const DEFAULT_SEARCH_PAGE_SIZE = 100;
const DEFAULT_SUMMARY_PAGE_SIZE = 8;
const SUMMARY_FIELDS = [
  'id',
  'division.id',
  'division.name',
  'createdAtDevice',
  'user.name',
  'user.email',
  'eventType.name',
];
/** Text fields a type-ahead query may match. Reserved characters are escaped. */
const SUMMARY_TEXT_FIELDS = ['user.name', 'user.email', 'eventType.name', 'division.name'];

/** Maps a related entity to the event Elastic field that references its id. */
const ENTITY_EVENT_ID_FIELD: Record<string, string> = {
  [FSCollections.EVENTS]: 'id',
  [FSCollections.USERS]: 'user.id',
  [FSCollections.SCHEDULES]: 'execution.schedule.id',
};

class EventService {
  private repository: EventRepository;

  constructor() {
    this.repository = new EventRepository();
  }

  /**
   * Resolves event document ids (grouped by division) matching the given
   * constraints in a SINGLE Elastic query:
   * - `searchTerm`: full-text search (backend multi_match — plain text).
   * - `entity`/`entityId`: term filter on the field referencing that entity
   *   (a schedule's events, a user's events, or one event by id).
   * When both are provided the backend ANDs them (term → filter, text → must),
   * so results are correct without any client-side intersection.
   */
  async searchIds({
    orgId,
    divIds,
    searchTerm,
    entity,
    entityId,
    pageSize = DEFAULT_SEARCH_PAGE_SIZE,
    from = 0,
    exactMatch = false,
  }: EventSearchIdsParams): Promise<EventSearchIdsResult | null> {
    return safeExecute(
      async () => {
        if (!orgId) throw new Error('Organization ID is required');

        const trimmedSearch = searchTerm?.trim() ?? '';
        const entityField = entity ? ENTITY_EVENT_ID_FIELD[entity] : undefined;
        const hasEntityFilter = Boolean(entityField && entityId);

        if (!trimmedSearch && !hasEntityFilter) {
          return { items: [], total: 0, idsByDivision: {} };
        }

        const filters: ElasticQueryParams['filters'] = {};
        if (trimmedSearch) {
          filters.textSearch = buildTextSearch(trimmedSearch, exactMatch);
        }
        if (hasEntityFilter) {
          filters.term = { [entityField as string]: entityId };
        }

        const params: ElasticQueryParams = {
          entity: FSCollections.EVENTS,
          organizationId: orgId,
          divisionIds: divIds,
          filters,
          pagination: { from, size: pageSize },
          sort: [{ field: 'createdAtDevice._seconds', order: 'desc' }],
          fields: ['id', 'division.id'],
        };

        const response = await this.repository.list(params);
        return EventMapper.fromElasticSearchIdsResponse(response);
      },
      'EventService.searchIds',
      { orgId, entity, pageSize, from },
    );
  }

  /**
   * Latest events matching a free-text search, with just enough fields to
   * label a result row (global sidebar search). The term is a partial match
   * on the summary text fields, with query_string reserved characters escaped.
   */
  async searchSummaries({
    orgId,
    divIds,
    searchTerm,
    pageSize = DEFAULT_SUMMARY_PAGE_SIZE,
  }: EventSearchSummariesParams): Promise<EventSearchSummariesResult | null> {
    return safeExecute(
      async () => {
        if (!orgId) throw new Error('Organization ID is required');

        const textSearch = buildPartialMatch(SUMMARY_TEXT_FIELDS, searchTerm);
        if (!textSearch) {
          return { items: [], total: 0 };
        }

        const params: ElasticQueryParams = {
          entity: FSCollections.EVENTS,
          organizationId: orgId,
          divisionIds: divIds,
          filters: { textSearch },
          pagination: { from: 0, size: pageSize },
          sort: [{ field: 'createdAtDevice._seconds', order: 'desc' }],
          fields: SUMMARY_FIELDS,
        };

        const response = await this.repository.list<EventElasticSearchSummarySource>(params);
        return EventMapper.fromElasticSearchSummariesResponse(response);
      },
      'EventService.searchSummaries',
      { orgId, pageSize },
    );
  }

  async delete(orgId: string, divId: string, eventId: string): Promise<void> {
    return safeExecute(
      async () => {
        if (!orgId || !divId || !eventId) {
          throw new Error('Organization, division and event ID are required');
        }
        await this.repository.delete(orgId, divId, eventId);
      },
      'EventService.delete',
      { orgId, divId, eventId },
    );
  }
}

export default EventService;
